// Rank System for Mind Warriors Dojo Theme
// Maps user levels to warrior ranks

export interface Rank {
  min: number;
  max: number;
  name: string;
  icon: string;
  color: string; // Tailwind color class prefix (e.g., "zinc", "blue")
  description: string;
}

export const RANKS: Record<string, Rank> = {
  APPRENTICE: {
    min: 1,
    max: 5,
    name: "Apprentice",
    icon: "🥋",
    color: "zinc",
    description: "Beginning the journey",
  },
  WARRIOR: {
    min: 6,
    max: 15,
    name: "Warrior",
    icon: "⚔️",
    color: "blue",
    description: "Proven in training",
  },
  MASTER: {
    min: 16,
    max: 30,
    name: "Master",
    icon: "🛡️",
    color: "purple",
    description: "Deep understanding",
  },
  GRANDMASTER: {
    min: 31,
    max: 50,
    name: "Grandmaster",
    icon: "👑",
    color: "yellow",
    description: "Elite mastery",
  },
  LEGEND: {
    min: 51,
    max: 75,
    name: "Legend",
    icon: "✨",
    color: "cyan",
    description: "Legendary skill",
  },
  CHAOS_SLAYER: {
    min: 76,
    max: 9999,
    name: "Chaos Slayer",
    icon: "🔥",
    color: "red",
    description: "Ultimate mastery",
  },
};

export interface RankInfo extends Rank {
  key: string;
}

/**
 * Get the rank information for a given level
 */
export function getLevelRank(level: number): RankInfo {
  for (const [key, rank] of Object.entries(RANKS)) {
    if (level >= rank.min && level <= rank.max) {
      return { ...rank, key };
    }
  }
  // Fallback to APPRENTICE if level is somehow invalid
  return { ...RANKS.APPRENTICE, key: "APPRENTICE" };
}

/**
 * Get progress to next rank (0-100%)
 */
export function getRankProgress(level: number): number {
  const currentRank = getLevelRank(level);
  const levelsInRank = currentRank.max - currentRank.min + 1;
  const levelsCompleted = level - currentRank.min;
  return Math.round((levelsCompleted / levelsInRank) * 100);
}

/**
 * Get the next rank from current level
 */
export function getNextRank(level: number): RankInfo | null {
  const currentRank = getLevelRank(level);
  const rankKeys = Object.keys(RANKS);
  const currentIndex = rankKeys.indexOf(currentRank.key);

  if (currentIndex < rankKeys.length - 1) {
    const nextKey = rankKeys[currentIndex + 1];
    return { ...RANKS[nextKey], key: nextKey };
  }

  return null; // Already at max rank
}

/**
 * Get levels needed to reach next rank
 */
export function getLevelsToNextRank(level: number): number {
  const currentRank = getLevelRank(level);
  return currentRank.max - level + 1;
}

/**
 * Get all ranks in order
 */
export function getAllRanks(): RankInfo[] {
  return Object.entries(RANKS).map(([key, rank]) => ({ ...rank, key }));
}

/**
 * Format rank display string (e.g., "Master (Level 24)")
 */
export function formatRankDisplay(level: number): string {
  const rank = getLevelRank(level);
  return `${rank.name} (Level ${level})`;
}
